import React, { useState, useRef, useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useChatMessages } from "../hooks/queries";
import { Reply, X } from "lucide-react";
import { sendChatMessage, markChatRead, Chat, ChatMessage, ChatMessageReply } from "../api/api";
import { useAuth } from "../hooks/useAuth";
import { UserAvatar } from "./ui/user-avatar";
import { cn } from "@/lib/utils";

function visibilityHint(chat: Chat, viewerId: number | undefined): string {
  if (chat.job_opening_id == null) {
    if (chat.archived || chat.user_id == null) {
      return `Gearchiveerd gesprek met ${chat.partner_name}.`;
    }
    if (chat.user_id === viewerId) {
      return "Privégesprek tussen jou en Waves.";
    }
    return `Privégesprek tussen Waves en ${chat.user_name ?? chat.partner_name}.`;
  }
  return `Zichtbaar voor Waves, beheerders van ${chat.partner_name} en hiring managers met toegang tot deze vestiging.`;
}

function timeLabel(iso: string): string {
  return new Date(iso).toLocaleTimeString("nl-NL", { hour: "2-digit", minute: "2-digit" });
}

function dayLabel(iso: string): string {
  const d = new Date(iso);
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);
  if (d.toDateString() === today.toDateString()) return "Vandaag";
  if (d.toDateString() === yesterday.toDateString()) return "Gisteren";
  return d.toLocaleDateString("nl-NL", { weekday: "long", day: "numeric", month: "long" });
}

// Single-line preview of a replied-to message for the quote and the reply bar.
function quotePreview(body: string): string {
  const line = body.replace(/\s+/g, " ").trim();
  return line.length > 120 ? `${line.slice(0, 120)}…` : line;
}

interface ChatThreadProps {
  chat: Chat;
  onSent?: () => void;
  /** Read-only threads (e.g. the admin Gesprekken overview) hide the composer and don't mark messages as read. */
  readOnly?: boolean;
}

export default function ChatThread({ chat, onSent, readOnly = false }: ChatThreadProps) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { data } = useChatMessages(chat.id);
  const messages: ChatMessage[] = data?.messages ? [...data.messages].reverse() : [];
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [replyTo, setReplyTo] = useState<ChatMessageReply | null>(null);
  const [highlightedId, setHighlightedId] = useState<number | null>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const el = messagesContainerRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [chat.id, messages.length]);

  useEffect(() => {
    setReplyTo(null);
  }, [chat.id]);

  useEffect(() => {
    if (highlightedId == null) return;
    const t = setTimeout(() => setHighlightedId(null), 1500);
    return () => clearTimeout(t);
  }, [highlightedId]);

  function startReply(m: ChatMessage) {
    setReplyTo({ id: m.id, sender_id: m.sender_id, sender_name: m.sender_name, body: m.body });
    inputRef.current?.focus();
  }

  // Scrolls to the quoted original if it's among the loaded messages.
  function jumpToMessage(id: number) {
    const el = messagesContainerRef.current?.querySelector(`[data-message-id="${id}"]`);
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    setHighlightedId(id);
  }

  useEffect(() => {
    if (readOnly) return;
    markChatRead(chat.id)
      .then(() => {
        queryClient.invalidateQueries({ queryKey: ["chats"] });
        queryClient.invalidateQueries({ queryKey: ["chats", "unread-count"] });
      })
      .catch(() => {
        // best-effort — a failed read-marker never blocks the UI
      });
  }, [chat.id, messages.length, queryClient, readOnly]);

  async function handleSend() {
    const body = draft.trim();
    if (!body || sending) return;
    setSending(true);
    try {
      await sendChatMessage(chat.id, body, replyTo?.id);
      setDraft("");
      setReplyTo(null);
      queryClient.invalidateQueries({ queryKey: ["chat-messages", chat.id] });
      queryClient.invalidateQueries({ queryKey: ["chats"] });
      onSent?.();
    } catch {
      // the global API client's error toast already surfaced this
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div ref={messagesContainerRef} data-tour="chat-thread" className="flex min-h-0 flex-1 flex-col gap-1 overflow-y-auto px-5 py-5">
        <div className="pb-3 text-center text-[11px] leading-snug text-waves-muted">{visibilityHint(chat, user?.id)}</div>
        {messages.length === 0 ? (
          <div className="py-8 text-center text-sm text-waves-muted">Nog geen berichten — stel gerust je vraag.</div>
        ) : (
          messages.map((m, i) => {
            const mine = m.sender_id === user?.id;
            const newDay = i === 0 || new Date(messages[i - 1].created_at).toDateString() !== new Date(m.created_at).toDateString();
            const sameSenderAsNext = i + 1 < messages.length && messages[i + 1].sender_id === m.sender_id
              && new Date(messages[i + 1].created_at).toDateString() === new Date(m.created_at).toDateString();
            return (
              <React.Fragment key={m.id}>
                {newDay && (
                  <div className="flex items-center gap-3 pb-3 pt-2">
                    <div className="h-px flex-1 bg-waves-line" />
                    <div className="text-[11px] font-bold uppercase tracking-label text-waves-muted">{dayLabel(m.created_at)}</div>
                    <div className="h-px flex-1 bg-waves-line" />
                  </div>
                )}
                <div className={cn("flex flex-col gap-1", sameSenderAsNext ? "pb-1" : "pb-2.5")}>
                  <div
                    data-message-id={m.id}
                    className={cn("group flex items-end gap-2", mine ? "flex-row-reverse justify-start" : "justify-start")}
                  >
                    {!mine && (
                      <div className="w-7 shrink-0">
                        {!sameSenderAsNext && <UserAvatar name={m.sender_name} size={28} />}
                      </div>
                    )}
                    <div
                      className={cn(
                        "max-w-[78%] whitespace-pre-wrap break-words px-[15px] py-[11px] text-sm leading-[1.55] transition-shadow",
                        mine ? "rounded-[16px_16px_4px_16px] bg-waves-ink text-white" : "rounded-[16px_16px_16px_4px] bg-waves-mist text-waves-ink",
                        highlightedId === m.id && "ring-2 ring-waves-zee",
                      )}
                    >
                      {m.reply_to && (
                        <button
                          type="button"
                          onClick={() => jumpToMessage(m.reply_to!.id)}
                          className={cn(
                            "mb-1.5 block w-full rounded-md border-l-[3px] px-2.5 py-1.5 text-left text-xs leading-snug",
                            mine ? "border-waves-zee bg-white/10 text-white/80" : "border-waves-zee bg-white/70 text-waves-muted",
                          )}
                        >
                          <span className={cn("block font-semibold", mine ? "text-white" : "text-waves-ink")}>
                            {m.reply_to.sender_id === user?.id ? "Jij" : m.reply_to.sender_name}
                          </span>
                          <span className="line-clamp-2">{quotePreview(m.reply_to.body)}</span>
                        </button>
                      )}
                      {m.body}
                    </div>
                    {!readOnly && !chat.archived && (
                      <button
                        type="button"
                        onClick={() => startReply(m)}
                        aria-label={`Reageer op bericht van ${m.sender_name}`}
                        title="Reageer"
                        className="mb-1 flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-waves-muted opacity-0 transition-opacity hover:bg-waves-mist hover:text-waves-ink focus-visible:opacity-100 group-hover:opacity-100 [@media(hover:none)]:opacity-100"
                      >
                        <Reply className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                  {!sameSenderAsNext && (
                    <div className={cn("flex gap-2 font-mono text-[11px] text-waves-muted", mine ? "justify-end" : "justify-start pl-9")}>
                      {!mine && <span className="font-sans font-semibold">{m.sender_name}</span>}
                      <span>{timeLabel(m.created_at)}</span>
                    </div>
                  )}
                </div>
              </React.Fragment>
            );
          })
        )}
      </div>
      {!readOnly && chat.archived && (
        <div className="shrink-0 border-t border-waves-line px-4 py-3 text-center text-xs text-waves-muted">
          Dit gesprek is gearchiveerd en kan alleen nog gelezen worden.
        </div>
      )}
      {!readOnly && !chat.archived && replyTo && (
        <div className="flex shrink-0 items-center gap-3 border-t border-waves-line bg-waves-mist/60 px-4 py-2">
          <Reply className="h-4 w-4 shrink-0 text-waves-zee" />
          <div className="min-w-0 flex-1 text-xs leading-snug">
            <div className="font-semibold text-waves-ink">
              Reageren op {replyTo.sender_id === user?.id ? "jezelf" : replyTo.sender_name}
            </div>
            <div className="truncate text-waves-muted">{quotePreview(replyTo.body)}</div>
          </div>
          <button
            type="button"
            onClick={() => setReplyTo(null)}
            aria-label="Reactie annuleren"
            className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-waves-muted hover:bg-white hover:text-waves-ink"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}
      {!readOnly && !chat.archived && (
        <div data-tour="chat-input" className={cn("flex shrink-0 items-center gap-2 px-4 py-3", !replyTo && "border-t border-waves-line")}>
          <input
            ref={inputRef}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleSend();
              } else if (e.key === "Escape" && replyTo) {
                e.preventDefault();
                setReplyTo(null);
              }
            }}
            placeholder="Typ een bericht…"
            aria-label="Bericht"
            className="h-11 min-w-0 flex-1 rounded-pill border border-waves-line bg-white px-[18px] text-sm text-waves-ink outline-none placeholder:text-waves-muted focus:border-waves-zee focus:ring-2 focus:ring-waves-zee/25"
          />
          <button
            onClick={handleSend}
            disabled={sending || !draft.trim()}
            className="h-11 shrink-0 rounded-pill bg-waves-koraal px-5 text-sm font-bold text-white transition-colors hover:bg-waves-koraal-hover disabled:bg-waves-koraal-disabled"
          >
            Stuur
          </button>
        </div>
      )}
    </div>
  );
}
